// ---------------------------------------------------------------------------
// EvalHid's fixed catalog: rainfall-runoff/snow models and their default
// parameters and state variables. NOT per-project data — it's the content
// of the template every new project starts from (confirmed by opening the
// original's own template file, EvalHid_Git/EvalHid/Lib/MPE_APO_base.sehd:
// 0 rows in APO_CUENCAS/APO_ZONAS, but APO_TIPO_MODELO and
// APO_DEF_PARAMETROS/APO_DEF_VAR_ESTADO already come complete — identical to
// a real project's like ModeloTuria's). That's why it's hardcoded here
// instead of asking the user for it.
// ---------------------------------------------------------------------------
import type { ParameterDefinition, StateVariableDefinition, ModelType } from "./types.ts";

export const MODEL_TYPES: ModelType[] = [
  { modelTypeCode: 1, modelTypeName: "Témez", kind: 0 },
  { modelTypeCode: 2, modelTypeName: "SAC", kind: 0 },
  { modelTypeCode: 3, modelTypeName: "HBV", kind: 0 },
  { modelTypeCode: 4, modelTypeName: "GR2M", kind: 0 },
  { modelTypeCode: 5, modelTypeName: "GR4J", kind: 0 },
  { modelTypeCode: 6, modelTypeName: "AWBM", kind: 0 },
  { modelTypeCode: 1, modelTypeName: "Nieve1", kind: 1 },
  { modelTypeCode: 2, modelTypeName: "Nieve2", kind: 1 },
];

function p(
  parameterCode: number,
  name: string,
  modelTypeCode: number,
  kind: number,
  defaultValue: number,
  units: string,
  longName: string | null
): ParameterDefinition {
  return {
    parameterCode,
    name,
    modelTypeCode,
    kind,
    defaultValue,
    units,
    longName,
  };
}

export const PARAMETER_DEFINITIONS: ParameterDefinition[] = [
  // Témez (1/0)
  p(101, "Hmax", 1, 0, 150, "mm", "Humedad máxima en el suelo"),
  p(102, "C", 1, 0, 0.3, "adim", "Parámetro de excedente"),
  p(103, "Imax", 1, 0, 230, "mm", "Capacidad máxima de infiltración"),
  p(104, "Alfa", 1, 0, 0.23, "1/tiempo", "Coeficiente de recesión del acuífero"),
  // SAC (2/0)
  p(201, "Uztwm", 2, 0, 60, "mm", "Capacidad de agua en tensión zona superior"),
  p(202, "Uzfwm", 2, 0, 35, "mm", "Capacidad de agua libre zona superior"),
  p(203, "Uzk", 2, 0, 0.3, "1/tiempo", "Tasa de salida de agua libre en zona siperior"),
  p(204, "Pctim", 2, 0, 0, "1/1", "Área impermeable mínima"),
  p(205, "Adimp", 2, 0, 0.05, "1/1", "Área impermeable adicional"),
  p(206, "Riva", 2, 0, 0, "1/1", "Área vegetación Riparian"),
  p(207, "Zperc", 2, 0, 2.5, "adim", "Coeficiente de Máxima tasa de percolación"),
  p(208, "Rexp", 2, 0, 2.5, "adim", "Exponente equación Percolación"),
  p(209, "Lztwm", 2, 0, 120, "mm", "Capacidad de agua en tensión zona baja"),
  p(210, "Lzfsm", 2, 0, 50, "mm", "Capacidad suplementaria de agua libre zona baja"),
  p(211, "Lzfpm", 2, 0, 45, "mm", "Capacidad primaria de agua libre zona baja"),
  p(212, "Lzsk", 2, 0, 0.1, "1/tiempo", "Tasa suplementaria de salida"),
  p(213, "Lzpk", 2, 0, 0.01, "1/tiempo", "Tasa primaria de salida"),
  p(
    214,
    "Pfree",
    2,
    0,
    0.2,
    "adim",
    "Fracción de agua percolada que va directamente a la zona baja de almacenamiento de agua libre"
  ),
  p(215, "Side", 2, 0, 0, "adim", "Coeficiente de recarga subterránea al flujo base"),
  p(
    216,
    "Saved",
    2,
    0,
    0.1,
    "adim",
    "Fracción de agua libre en zona baja que no es transferible a agua en tensión en la zona baja"
  ),
  // HBV (3/0)
  p(301, "Beta", 3, 0, 3, "adim", "Parámtero del modelo"),
  p(302, "FC", 3, 0, 180, "mm", "Capacidad de campo"),
  p(303, "Pwp", 3, 0, 105, "mm", "Punto de marchitez permanente del suelo"),
  p(304, "Lmax", 3, 0, 6, "mm", "Umbral de flujo subsuperficial"),
  p(305, "K0", 3, 0, 0.13, "1/tiempo", "Constante de almacenamietno subterráneo"),
  p(306, "K1", 3, 0, 0.05, "1/tiempo", "Constante de almacenamiento de interflujo"),
  // K2 (0.2) > K0 (0.13) aquí incumple la regla K0>K1>K2 (flujo rápido > interflujo >
  // flujo base) que documenta el propio Manual Técnico — pero es el valor real que trae
  // MPE_APO_base.sehd, la plantilla de fábrica del .exe original (confirmado en
  // test/baseTemplate.test.ts, que compara byte a byte contra ese fichero). Se mantiene
  // tal cual, coherente con la política del resto del motor de replicar el .exe real
  // incluso cuando el resultado no encaja con su propia documentación.
  p(307, "K2", 3, 0, 0.2, "1/tiempo", "Constante de almacenamiento de flujo base"),
  p(308, "Kperc", 3, 0, 0.1, "1/tiempo", "Constante de almacenamiento de percolación"),
  // GR2M (4/0)
  p(401, "X1", 4, 0, 350, "mm", "Capacidad del tanque de producción."),
  p(402, "X2", 4, 0, 0, "mm", "Coeficiente de intercambio de agua"),
  // GR4J (5/0)
  p(501, "X1", 5, 0, 350, "mm", " Capacidad del tanque de producción."),
  p(502, "X2", 5, 0, 0, "mm", "Coeficiente de intercambio de agua"),
  p(503, "X3", 5, 0, 90, "mm", " Capacidad máxima del tanque de tránsito"),
  p(504, "X4", 5, 0, 1.7, "tiempo", "Tiempo base del hidrograma unitario"),
  // AWBM (6/0)
  p(
    601,
    "Cave",
    6,
    0,
    25,
    "mm",
    "Capacidad media del suelo. Posteriormente repartida entre c1, c2 y c3"
  ),
  p(602, "Bfi", 6, 0, 0.8, "adim", null),
  p(604, "Kb", 6, 0, 0.02, "1/tiempo", "Coeficiente de desacarga del tanque inferior"),
  p(603, "Ksurf", 6, 0, 0.1, "1/tiempo", "Coeficiente de desacarga del tanque superior"),
  // Nieve1 (1/1)
  p(101, "TT", 1, 1, 0, "ºC", "Umbral de temperatura"),
  p(102, "DD", 1, 1, 3, "mm/ºC.tiempo", "Factor Grados-Día"),
  // Nieve2 (2/1)
  p(201, "An", 2, 1, 1, "m/s/ºC", "Coeficiente grado-día"),
  p(202, "Bp", 2, 1, 0.0125, "s/m", "Coeficiente de fusión de la temperatura"),
  p(203, "Ocr", 2, 1, 0.1, "1/1", "Contenido crítico relativo de agua en el stock de nieve"),
  p(204, "Tcp1", 2, 1, 0, "ºC", "Temperatura crítica de mínima de precipitación líquida"),
  p(205, "Tcp2", 2, 1, 6, "ºC", "Temperatura crítica máxima para la precipitación sólida"),
  p(206, "Tcr", 2, 1, 0, "ºC", "Temperatura crítica de fusión de nieve"),
];

function v(
  stateVariableCode: number,
  name: string,
  modelTypeCode: number,
  kind: number,
  defaultValue: number,
  units: string | null,
  longName: string | null
): StateVariableDefinition {
  return {
    stateVariableCode,
    name,
    modelTypeCode,
    kind,
    defaultValue,
    units,
    longName,
  };
}

export const STATE_VARIABLE_DEFINITIONS: StateVariableDefinition[] = [
  v(11, "Ho", 1, 0, 20, "mm", "Humedad inicial en el suelo"),
  v(12, "Vo", 1, 0, 35, "mm", "Almacenamiento inicial en el acuífero"),
  v(21, "uztwc", 2, 0, 40, "mm", "Contenido de Agua en tensión en Zona superior"),
  v(22, "uzfwc", 2, 0, 20, "mm", "Contenido de Agua Libre en Zona superior"),
  v(23, "lztwc", 2, 0, 40, "mm", "Contenido de Agua en tensión en Zona baja"),
  v(24, "lzfsc", 2, 0, 40, "mm", "Contenido de Agua Libre suplementaria en Zona Baja"),
  v(25, "lzfpc", 2, 0, 40, "mm", "Contenido de Agua Libre primaria en Zona Baja"),
  v(26, "adim", 2, 0, 0, "mm", "Contenido de agua en tensión en el área ADIMP"),
  v(31, "Ho", 3, 0, 50, "mm", "Humedad del suelo"),
  v(32, "S1", 3, 0, 2, "mm", "Almacenamiento tanque superior"),
  v(33, "S2", 3, 0, 50, "mm", "Almacenamiento acuífero"),
  v(41, "So", 4, 0, 50, "mm", "Contenido inicial del tanque de producción."),
  v(42, "Ro", 4, 0, 30, "mm", "Contenido inicial del tanque de descarga."),
  v(51, "So", 5, 0, 150, "mm", null),
  v(52, "Ro", 5, 0, 30, "mm", null),
  v(61, "Have", 6, 0, 35, "mm", "Altura media de los tanques superiores"),
  v(63, "Vacu", 6, 0, 50, "mm", "Almacenamiento acuífero"),
  v(62, "Vsup", 6, 0, 20, null, "Almacenamientpo tanque superior"),
  v(11, "No", 1, 1, 25, "mm", "Contenido de nieve inicial"),
  v(21, "Ho", 2, 1, 20, "mm", "Contenido de nieve"),
  v(22, "Oo", 2, 1, 0.1, "1/1", "Proporción de agua en nieve"),
];
